import { EventType } from "@/analytics/analytics-events";
import { MeasureLabel } from "@/components/r3f/renderers/measurements/measure-label";
import { useViewOverlayRef } from "@/hooks/use-view-overlay-ref";
import { PointCloudObject } from "@/object-cache";
import {
  addAnalysisLabel,
  PointCloudAnalysis,
} from "@/store/point-cloud-analysis-tool-slice";
import { useAppDispatch } from "@/store/store-hooks";
import {
  computeLabelPosition,
  getAnalysisReferencePlane,
} from "@/utils/colormap-analysis-utils";
import {
  computeNdcCoordinates,
  getPickedPoint,
  useOverrideCursor,
  useThreeEventTarget,
} from "@faro-lotv/app-component-toolbox";
import { Analytics } from "@faro-lotv/foreign-observers";
import { generateGUID } from "@faro-lotv/foundation";
import {
  createProjectedPolygon2d,
  isPointInProjectedPolygon,
  ProjectedPolygon2d,
} from "@faro-lotv/lotv";
import { useThree } from "@react-three/fiber";
import { useCallback, useEffect, useMemo, useState, type JSX } from "react";
import { Plane, Raycaster, Vector3 } from "three";
import { getCadAnalysisReference } from "./cad-analysis/cad-analysis-runtime";

type PointCloudAnalysisLabelToolProps = {
  /** True if this tool is the active one */
  isActive: boolean;

  /** The active analysis object */
  activeAnalysis: PointCloudAnalysis;

  /** The active point cloud object */
  activePointCloud: PointCloudObject;
};

/** @returns The tool used to create labels on an existing analysis */
export function PointCloudAnalysisLabelTool({
  isActive,
  activeAnalysis,
  activePointCloud,
}: PointCloudAnalysisLabelToolProps): JSX.Element | null {
  const dispatch = useAppDispatch();

  const referencePlane = useMemo(() => {
    const plane = getAnalysisReferencePlane(activeAnalysis);
    if (!plane) return;
    return new Plane().setFromNormalAndCoplanarPoint(plane.normal, plane.point);
  }, [activeAnalysis]);

  const [labelPosition, setLabelPosition] = useState<Vector3>();
  const [labelDistance, setLabelDistance] = useState<number>(0.0);

  const [projectedPolygon2d, setProjectedPolygon2d] =
    useState<ProjectedPolygon2d>();
  useEffect(() => {
    // eslint-disable-next-line func-style -- FIXME
    const computePolygon = async (): Promise<void> => {
      if (activeAnalysis.kind === "cad") {
        setProjectedPolygon2d(undefined);
        return;
      }
      const polygon = activeAnalysis.polygonSelection.map((p) =>
        new Vector3().fromArray(p),
      );
      const projPolygon = await createProjectedPolygon2d(polygon);
      setProjectedPolygon2d(projPolygon);
    };
    computePolygon().catch((error) => {
      console.error(error);
      setProjectedPolygon2d(undefined);
    });
  }, [activeAnalysis.polygonSelection, activeAnalysis.kind]);

  const eventTarget = useThreeEventTarget();
  const [raycaster] = useState(() => new Raycaster());
  const camera = useThree((state) => state.camera);

  const pickLabelPositionOnAnalysis = useCallback(
    (event: PointerEvent | MouseEvent): Vector3 | undefined => {
      if (!isActive || (activeAnalysis.kind !== "cad" && !projectedPolygon2d)) {
        return;
      }

      const mouse = computeNdcCoordinates(
        event.clientX,
        event.clientY,
        eventTarget,
      );
      raycaster.setFromCamera(mouse, camera);
      const oldRealTimeRaycasting = activePointCloud.realTimeRaycasting;
      activePointCloud.realTimeRaycasting = false;
      const hits = raycaster.intersectObject(activePointCloud);
      activePointCloud.realTimeRaycasting = oldRealTimeRaycasting;
      if (!hits.length) return;

      const pickedPoint = getPickedPoint(hits[0]);
      const distanceThreshold = activeAnalysis.searchDistance;
      if (activeAnalysis.kind === "cad") {
        const reference = getCadAnalysisReference(
          activePointCloud,
          activeAnalysis.id,
        );
        return reference?.distance(pickedPoint, distanceThreshold) === undefined
          ? undefined
          : pickedPoint;
      }
      if (!projectedPolygon2d) return;
      if (
        isPointInProjectedPolygon(
          pickedPoint,
          projectedPolygon2d,
          distanceThreshold,
        )
      ) {
        return computeLabelPosition(activePointCloud, pickedPoint);
      }
    },
    [
      activeAnalysis.searchDistance,
      activeAnalysis.id,
      activeAnalysis.kind,
      activePointCloud,
      camera,
      eventTarget,
      isActive,
      projectedPolygon2d,
      raycaster,
    ],
  );

  const pointHovered = useCallback(
    (event: PointerEvent): void => {
      const cadReference = getCadAnalysisReference(
        activePointCloud,
        activeAnalysis.id,
      );
      if (!referencePlane && !cadReference) {
        setLabelPosition(undefined);
        return;
      }
      const labelPosition = pickLabelPositionOnAnalysis(event);
      if (!labelPosition) {
        setLabelPosition(undefined);
        return;
      }

      event.stopPropagation();
      setLabelPosition(labelPosition);
      const distance =
        referencePlane?.distanceToPoint(labelPosition) ??
        cadReference?.distance(labelPosition, activeAnalysis.searchDistance);
      if (distance !== undefined) setLabelDistance(distance);
    },
    [
      pickLabelPositionOnAnalysis,
      referencePlane,
      activePointCloud,
      activeAnalysis.id,
      activeAnalysis.searchDistance,
    ],
  );

  const pointClicked = useCallback(
    (event: MouseEvent): void => {
      const labelPosition = pickLabelPositionOnAnalysis(event);
      if (!labelPosition) return;

      Analytics.track(EventType.createColoredAnalysisLabel);

      event.stopPropagation();
      // remove preview so it won't show two labels at the same time
      setLabelPosition(undefined);
      dispatch(
        addAnalysisLabel({
          analysisId: activeAnalysis.id,
          label: {
            id: generateGUID(),
            position: labelPosition.toArray(),
          },
        }),
      );
    },
    [activeAnalysis.id, dispatch, pickLabelPositionOnAnalysis],
  );

  useEffect(() => {
    eventTarget.addEventListener("pointermove", pointHovered);
    eventTarget.addEventListener("click", pointClicked);
    return () => {
      eventTarget.removeEventListener("pointermove", pointHovered);
      eventTarget.removeEventListener("click", pointClicked);
    };
  }, [eventTarget, pickLabelPositionOnAnalysis, pointClicked, pointHovered]);

  useOverrideCursor("crosshair", isActive);

  const labelContainer = useViewOverlayRef();

  if (!isActive) return null;

  return (
    <MeasureLabel
      index={0}
      visible
      position={labelPosition}
      parentRef={labelContainer}
      distance={labelDistance}
      active
      transparent
      pointerEvents="none"
      placement="top"
      useSmallUnits
    />
  );
}
